import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { aplicarIndicadoresRegraTributaria, RegraTributariaIndicadoresUi } from '@shared/constants/motor-fiscal/regra-tributaria-indicadores.util';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { MaskService } from '@shared/services/mask.service';
import { CClassTribIbsCbsStruct } from '@shared/services/structs/motor-fiscal/c-class-trib-ibs-cbs.struct';
import { CstIbsCbsCclasstribIbsCbsStruct } from '@shared/services/structs/motor-fiscal/cst-ibs-cbs-cclasstrib-ibs-cbs.struct';
import { CstIbsCbsStruct } from '@shared/services/structs/motor-fiscal/cst-ibs-cbs.struct';

/**
 * Aba "IBS/CBS" do modal do item da venda (cópia da aba IBS/CBS do item da NF-e de
 * saída). Mesma estratégia da Regra Tributária: os indicadores do CST
 * e do cClassTrib decidem se o grupo é gerado e quais campos aparecem; a troca de CST limpa o que
 * sumiu e reposiciona o cClassTrib.
 */
@Component({
    selector: 'app-vd-item-aba-ibs-cbs',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        EmptyStateComponent,
    ],
    templateUrl: './vd-item-aba-ibs-cbs.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-ibs-cbs.component.css'],
})
export class VdItemAbaIbsCbsComponent implements OnInit, OnDestroy {
    /** O grupo `ibsCbs` do item. */
    @Input({ required: true }) public form!: FormGroup;
    @Input() public listaCstIbsCbs: CstIbsCbsStruct[] = [];
    @Input() public listaCclasstribIbsCbs: CClassTribIbsCbsStruct[] = [];
    @Input() public relacoesCstCclasstrib: CstIbsCbsCclasstribIbsCbsStruct[] = [];
    @Input() public isRecalculoPendente: boolean = false;

    public readonly percentMask = this.maskService.getPercentMask();

    private readonly destroy$ = new Subject<void>();

    constructor(private maskService: MaskService) {}

    public ngOnInit(): void {
        this.form.get('codigoCst')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.reposicionarCclasstrib());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get cstSelecionado(): CstIbsCbsStruct | undefined {
        return this.listaCstIbsCbs.find((c) => c.codigo === this.form?.get('codigoCst')?.value);
    }

    public get cclasstribSelecionado(): CClassTribIbsCbsStruct | undefined {
        return this.listaCclasstribIbsCbs.find((c) => c.codigo === this.form?.get('codigoClassificacaoTributaria')?.value);
    }

    public get indicadores(): RegraTributariaIndicadoresUi {
        return aplicarIndicadoresRegraTributaria(this.cstSelecionado?.indicadores, this.cclasstribSelecionado?.indicadores);
    }

    public get cclasstribsFiltrados(): CClassTribIbsCbsStruct[] {
        const cst = this.form?.get('codigoCst')?.value;

        if (!cst) return [];

        const codigos = new Set(this.relacoesCstCclasstrib.filter((r) => r.cstIbsCbs === cst).map((r) => r.cclasstribIbsCbs));

        return this.listaCclasstribIbsCbs.filter((c) => codigos.has(c.codigo));
    }

    public get gerarIbsCbs(): boolean {
        return !!this.cstSelecionado && this.indicadores.ibsCbs.ativo;
    }

    /** Troca feita pelo usuário: zera o que o novo CST deixou de exibir (sem gIBSCBS, sem alíquotas; sem gDif, sem diferimento). */
    public onCstSelecionado(): void {
        const campos: string[] = [];

        if (!this.gerarIbsCbs) campos.push('percentualAliquotaCbs', 'percentualAliquotaIbsUf', 'percentualAliquotaIbsMunicipal');
        if (!this.gerarIbsCbs || !this.indicadores.ibsCbs.ibsUf.diferimento) campos.push('percentualDiferimentoIbsUf');

        campos.forEach((campo) => this.form.get(campo)?.setValue(null, { emitEvent: false }));
    }

    /** Alíquotas efetivas aparecem quando apuradas; reduções, só quando existem. */
    public get aliquotasApuradas(): Array<{ rotulo: string; valor: number }> {
        const efetivas: Array<[string, string]> = [
            ['Alíquota efetiva CBS', 'percentualAliquotaEfetivaCbs'],
            ['Alíquota efetiva IBS UF', 'percentualAliquotaEfetivaIbsUf'],
            ['Alíquota efetiva IBS Municipal', 'percentualAliquotaEfetivaIbsMunicipal'],
        ];
        const reducoes: Array<[string, string]> = [
            ['Redução CBS', 'percentualReducaoCbs'],
            ['Redução IBS UF', 'percentualReducaoIbsUf'],
            ['Redução IBS Municipal', 'percentualReducaoIbsMunicipal'],
        ];
        const valor = (campo: string): number | null => this.form.get(campo)?.value ?? null;

        return [
            ...efetivas.filter(([, campo]) => valor(campo) != null),
            ...reducoes.filter(([, campo]) => !!valor(campo)),
        ].map(([rotulo, campo]) => ({ rotulo, valor: valor(campo)! }));
    }

    /** Ao trocar o CST, reposiciona o cClassTrib na primeira opção válida. */
    private reposicionarCclasstrib(): void {
        const validos = this.cclasstribsFiltrados;
        const atual = this.form.get('codigoClassificacaoTributaria')?.value;

        if (!validos.some((c) => c.codigo === atual)) {
            this.form.get('codigoClassificacaoTributaria')?.setValue(validos[0]?.codigo ?? null);
        }
    }
}
